import { emDash } from '@/components/shared/utils/utils';
import { ReactNode, useId, useState } from 'react';
import {
    CardChevron,
    CardToggle,
    ChangedDDDRow,
    CollapsibleCardHeader,
    DDDCard,
    DDDCardPulled,
    DDDCardTitle,
    DDDRow,
    DDDRowLabel,
    DDDRowList,
    DDDRowValue,
} from '../Applicant.StyledComponents';
import { CompareCardProvider, useIsRowChanged } from './ApplicationCompare';

interface ApplicantInfoCardProps {
    title: string;
    subTitle?: ReactNode;
    children?: ReactNode;
}

// Collapsible Applicant tab card, styled to match the DDD tab's vendor cards.
export const ApplicantInfoCard = ({ title, subTitle, children }: ApplicantInfoCardProps) => {
    const [isOpen, setIsOpen] = useState(true);
    const bodyId = useId();
    const testId = title.toLowerCase().replace(' ', '-');

    return (
        <DDDCard data-testid={testId}>
            <CollapsibleCardHeader $collapsed={!isOpen}>
                <DDDCardTitle>
                    <CardToggle
                        type="button"
                        onClick={() => setIsOpen((open) => !open)}
                        aria-expanded={isOpen}
                        aria-controls={bodyId}
                        data-testid={`${testId}-toggle`}
                    >
                        <CardChevron aria-hidden>{isOpen ? '▾' : '▸'}</CardChevron>
                        {title}
                    </CardToggle>
                </DDDCardTitle>
                {subTitle ? <DDDCardPulled>{subTitle}</DDDCardPulled> : null}
            </CollapsibleCardHeader>
            <DDDRowList id={bodyId} hidden={!isOpen}>
                {/* Owner cards share a title, so the subtitle keeps their rows apart in a compare. */}
                <CompareCardProvider value={typeof subTitle === 'string' ? `${title} ${subTitle}` : title}>
                    {children}
                </CompareCardProvider>
            </DDDRowList>
        </DDDCard>
    );
};

interface ApplicantInfoRowProps {
    label: string;
    value?: ReactNode;
    defaultValue?: string;
    // Today's value, not a recorded one, so a compare never flags the row.
    isLive?: boolean;
    'data-testid'?: string;
}

export const ApplicantInfoRow = ({
    label,
    value,
    defaultValue = emDash,
    isLive = false,
    ...props
}: ApplicantInfoRowProps) => {
    const isChanged = useIsRowChanged(label, value, isLive);
    const Row = isChanged ? ChangedDDDRow : DDDRow;

    return (
        <Row {...props} data-changed={isChanged || undefined}>
            <DDDRowLabel>{label}</DDDRowLabel>
            <DDDRowValue>{value || defaultValue}</DDDRowValue>
        </Row>
    );
};
